// ------------------------------------------------------------
// LOCATIONS EDITOR
// Loads the selected agency's existing Location Information
// configuration for read-only inspection.
//
// VERSION 1 BUILD PHASE:
// GET only. No configuration changes are written to Mongo.
// ------------------------------------------------------------

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import Globals from "../../utils/globals";

// ============================================================================
// LOCATIONS EDITOR - READ-ONLY LOCATION TREE WITH COLLAPSE / EXPAND
// ============================================================================
// Recursively displays the nested Location Information structure.
//
// Nodes with children can be expanded or collapsed.
// DISPLAY ONLY: no editing, reordering, removing, or saving.
// ============================================================================
const LocationTreeNode = ({
    item,
    depth = 0,
    selectedNode,
    onSelect,
}) => {
    const [isOpen, setIsOpen] = useState(depth === 0);

    if (!item) return null;

    const title =
        item?.title?.en_US ||
        item?.value?.en_US ||
        item?.title ||
        item?.value ||
        "Untitled item";

    const children = Array.isArray(item?.items)
        ? item.items
        : [];

    const hasChildren = children.length > 0;

    // ------------------------------------------------------------
    // LOCATION TREE - DETERMINE WHETHER THIS NODE IS SELECTED
    // Uses the Mongo _id when available. Object identity is the
    // fallback for any legacy item without an _id.
    // ------------------------------------------------------------
    const isSelected =
        selectedNode?._id && item?._id
            ? String(selectedNode._id) === String(item._id)
            : selectedNode === item;

    return (
        <div
            style={{
                marginLeft: depth === 0 ? 0 : 28,
                marginTop: 8,
            }}
        >
            {/* ------------------------------------------------------------ */}
            {/* LOCATION TREE - SELECTABLE NODE ROW                         */}
            {/* Clicking the row selects this location item.                */}
            {/* Selected nodes are highlighted in blue.                     */}
            {/* ------------------------------------------------------------ */}
            <div
                onClick={() => onSelect(item)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 12px",

                    borderLeft: isSelected
                        ? "4px solid #0d6efd"
                        : depth > 0
                            ? "3px solid #d9e6ff"
                            : "3px solid #0d6efd",

                    background: isSelected
                        ? "#e7f1ff"
                        : depth === 0
                            ? "#f5f8ff"
                            : "#fafafa",

                    borderRadius: 4,
                    cursor: "pointer",
                }}
            >
                {/* -------------------------------------------------------- */}
                {/* LOCATION TREE - EXPAND / COLLAPSE CONTROL               */}
                {/* -------------------------------------------------------- */}
                {hasChildren ? (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen((current) => !current);
                        }}
                        title={isOpen ? "Collapse" : "Expand"}
                        style={{
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <span
                            className="material-icons"
                            style={{
                                fontSize: 22,
                                color: "#0d6efd",
                            }}
                        >
                            {isOpen
                                ? "keyboard_arrow_down"
                                : "keyboard_arrow_right"}
                        </span>
                    </button>
                ) : (
                    <div style={{ width: 22 }} />
                )}

                {/* -------------------------------------------------------- */}
                {/* LOCATION TREE - NODE LABEL                             */}
                {/* -------------------------------------------------------- */}
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {title}
                    </div>

                    {/* Temporary structural diagnostics while we build. */}
                    <div
                        style={{
                            marginTop: 3,
                            fontSize: 13,
                            color: "#6c757d",
                        }}
                    >
                        {item?.type && (
                            <span>
                                Type: {item.type}
                            </span>
                        )}

                        {children.length > 0 && (
                            <span>
                                {item?.type ? " · " : ""}
                                {children.length}{" "}
                                {children.length === 1
                                    ? "child"
                                    : "children"}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ------------------------------------------------------------ */}
            {/* LOCATION TREE - NESTED CHILDREN                            */}
            {/* Only render children while this node is expanded.          */}
            {/* ------------------------------------------------------------ */}
            {hasChildren && isOpen && (
                <div>
                    {children.map((child, childIndex) => (
                        <LocationTreeNode
                            key={
                                child?._id ||
                                child?.id ||
                                `${depth}-${childIndex}`
                            }
                            item={child}
                            depth={depth + 1}
                            selectedNode={selectedNode}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};


const LocationsEditor = () => {
    const { getAccessTokenSilently } = useAuth0();

    // ------------------------------------------------------------
    // SELECTED AGENCY
    // Get the agency selected in the Transit Watch console.
    // ------------------------------------------------------------
    const agencyId = useSelector(
        (state) => state.agencyStubs.selectedAgencyId
    );

    const agency = useSelector(
        (state) => state.agency
    );

    // ------------------------------------------------------------
    // PUBLIC INCIDENT REPORT SCHEMA
    // Locations live inside the public incident report schema.
    // This is the same schema-discovery pattern used by the
    // Incident Types Editor.
    // ------------------------------------------------------------
    const incidentReportSchema =
        agency?.details?.report_schemas?.find(
            (schema) =>
                schema?.type === "public" &&
                schema?.subtype === "incident"
        );

    const reportSchemaId = incidentReportSchema?._id || null;

    // ------------------------------------------------------------
    // LOCATIONS EDITOR STATE VARS
    // ------------------------------------------------------------
    const [locationSection, setLocationSection] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    // ------------------------------------------------------------
    // LOCATIONS EDITOR - SELECTED TREE NODE
    // Stores the location-tree item currently selected by the user.
    // Selection only for now; it does not modify the configuration.
    // ------------------------------------------------------------
    const [selectedNode, setSelectedNode] = useState(null);
    // ------------------------------------------------------------
    // LOCATIONS EDITOR - EDIT LOCATION CHOICE NAME
    // Holds the temporary English name while a Location Choice
    // is being edited.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo until a future Save feature.
    // ------------------------------------------------------------
    const [editingName, setEditingName] = useState(false);
    const [draftName, setDraftName] = useState("");

    // ------------------------------------------------------------
    // SELECTION LIST - EDIT LIST NAME / YES-NO QUESTION
    // Holds the temporary title while editing either:
    // 1. A normal Selection List name, or
    // 2. A Yes/No Question.
    //
    // The UI will use:
    // "Edit List Name" for a normal list.
    // "Edit Question" for a Yes/No question.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [editingListName, setEditingListName] = useState(false);
    const [draftListName, setDraftListName] = useState("");

    // ------------------------------------------------------------
    // TEXT ENTRY - EDIT TEXT
    // Holds temporary values while editing a Text Entry.
    //
    // This will support editing the field label/title and,
    // when present, its English prompt/placeholder.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [editingText, setEditingText] = useState(false);
    const [draftTextTitle, setDraftTextTitle] = useState("");
    const [draftTextPrompt, setDraftTextPrompt] = useState("");


    // ------------------------------------------------------------
    // LOCATION CHOICE - REMOVE CONFIRMATION
    // Holds the selected Location Choice while the user confirms
    // whether the entire location branch should be removed.
    // ------------------------------------------------------------
    const [removingLocationChoice, setRemovingLocationChoice] = useState(false);

    // ------------------------------------------------------------
    // YES / NO QUESTION - REMOVE CONFIRMATION
    // Controls the confirmation panel for removing an entire
    // Yes/No Question as one paired structure.
    //
    // Removing the question will also remove both Yes/No answers
    // and anything configured beneath either answer.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [removingYesNoQuestion, setRemovingYesNoQuestion] = useState(false);

    // ------------------------------------------------------------
    // TEXT ENTRY - REMOVE CONFIRMATION
    // Controls the confirmation panel for removing a selected
    // Text Entry such as "Describe Location" or "Train Car Number".
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [removingTextEntry, setRemovingTextEntry] = useState(false);

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - ADD NEW TOP-LEVEL LOCATION
    // Holds the temporary name while a new Location Choice is
    // being created under "Select Incident Location".
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [addingLocationChoice, setAddingLocationChoice] = useState(false);
    const [newLocationName, setNewLocationName] = useState("");
    // ------------------------------------------------------------
    // LOCATION CHOICE - ADD NESTED LOCATION LIST
    // Holds the temporary list name while the admin creates
    // another location list beneath the selected Location Choice.
    //
    // Example:
    // Union Station
    //   -> Area
    //      -> East Annex
    //      -> Mall Area
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [addingLocationList, setAddingLocationList] = useState(false);
    const [newLocationListName, setNewLocationListName] = useState("");

    // ------------------------------------------------------------
    // SELECTION LIST - ADD NEW CHOICE
    // Holds the temporary choice name while the admin adds a new
    // option beneath the currently selected Selection List.
    //
    // Example:
    // Area
    //   -> East Annex
    //   -> Mall Area
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [addingListChoice, setAddingListChoice] = useState(false);
    const [newListChoiceName, setNewListChoiceName] = useState("");

    // ------------------------------------------------------------
    // LOCATION CHOICE - ADD YES / NO QUESTION
    // Holds the temporary values while the admin creates a Yes/No
    // question beneath the selected Location Choice.
    //
    // Example:
    // On a Light Rail
    //   -> Are you in a train car?
    //      -> Yes, in a train car.
    //      -> No, not in a train car.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const [addingYesNoQuestion, setAddingYesNoQuestion] = useState(false);

    const [newYesNoQuestion, setNewYesNoQuestion] = useState("");
    const [newYesChoice, setNewYesChoice] = useState("");
    const [newNoChoice, setNewNoChoice] = useState("");

    // ------------------------------------------------------------
    // LOAD FUNCTION
    // LOCATIONS EDITOR - LOAD LOCATION INFORMATION
    // Loads the full Location Information section for the selected
    // agency's public incident report schema.
    //
    // READ ONLY FOR THIS BUILD STEP.
    // No changes are written to Mongo.
    // ------------------------------------------------------------
    const load = async () => {
        if (!agencyId || !reportSchemaId) return;

        setLoading(true);
        setLoadError("");

        try {
            const token = await getAccessTokenSilently();

            const url =
                `${Globals.apiEndpoint}/dashboard/agencies/${agencyId}` +
                `/report_schemas/${reportSchemaId}/locations`;

            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                setLoadError(
                    json?.message || "Failed to load location information"
                );
                return;
            }

            // --------------------------------------------------------
            // LOCATIONS EDITOR - STORE LOADED LOCATION SECTION
            // The backend returns the entire Location Information
            // section, including all nested lists, options and fields.
            // --------------------------------------------------------
            setLocationSection(
                json?.location_section || null
            );
        } catch (err) {
            console.error(
                "[LocationsEditor] load error:",
                err
            );

            setLoadError(
                "Failed to load location information"
            );
        } finally {
            setLoading(false);
        }
    };


    // ------------------------------------------------------------
    // LOCATIONS EDITOR - AUTO LOAD
    // Reloads the Location Information section whenever the
    // selected agency/report schema changes.
    // ------------------------------------------------------------
    useEffect(() => {
        load();
    }, [agencyId, reportSchemaId]);

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - IDENTIFY YES / NO LIST
    // A Yes/No question is stored as a normal list whose choices
    // begin with Yes and No. This lets the Config UI describe the
    // existing schema in human-friendly terms.
    // ------------------------------------------------------------
    const isYesNoList = (item) => {
        if (item?.type !== "list") return false;

        const choices = Array.isArray(item?.items)
            ? item.items
            : [];

        if (choices.length !== 2) return false;

        const titles = choices.map((choice) =>
            String(choice?.title?.en_US || "")
                .trim()
                .toLowerCase()
        );

        return (
            titles.some((title) => title.startsWith("yes")) &&
            titles.some((title) => title.startsWith("no"))
        );
    };

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - IDENTIFY YES / NO ANSWER
    // Determines whether a selected list_option is one of the two
    // answers directly beneath a Yes/No Question.
    //
    // Works with:
    // 1. Existing Mongo schemas that do not have editor_type.
    // 2. New locally-created Yes/No structures that do.
    //
    // This lets the editor protect the Yes/No pair from having
    // only one answer accidentally removed.
    // ------------------------------------------------------------
    const isYesNoAnswerNode = (targetNode) => {
        if (!targetNode || targetNode?.type !== "list_option") {
            return false;
        }

        // New locally-created Yes/No answers are explicitly marked.
        if (targetNode?.editor_type === "yes_no_answer") {
            return true;
        }

        const targetId = targetNode?._id
            ? String(targetNode._id)
            : null;

        if (!targetId) return false;

        // --------------------------------------------------------
        // SEARCH TREE FOR A YES / NO LIST CONTAINING THIS ANSWER
        // --------------------------------------------------------
        const searchItems = (items) => {
            if (!Array.isArray(items)) return false;

            for (const item of items) {
                if (
                    (
                        item?.editor_type === "yes_no_question" ||
                        isYesNoList(item)
                    ) &&
                    Array.isArray(item?.items)
                ) {
                    const containsTarget = item.items.some(
                        (child) =>
                            child?._id &&
                            String(child._id) === targetId
                    );

                    if (containsTarget) {
                        return true;
                    }
                }

                if (
                    Array.isArray(item?.items) &&
                    searchItems(item.items)
                ) {
                    return true;
                }
            }

            return false;
        };

        return searchItems(locationSection?.items);
    };

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - FRIENDLY CHILD DESCRIPTION
    // Converts child schema items into language an agency admin
    // can understand in the Selected Item panel.
    // ------------------------------------------------------------
    const describeConfiguredChild = (item) => {
        const title = item?.title?.en_US || "Untitled";

        if (
            item?.type === "text" &&
            title.toLowerCase() === "describe location"
        ) {
            return "Describe Location";
        }

        if (isYesNoList(item)) {
            return `${title} — Yes/No Question`;
        }

        if (item?.type === "list") {
            return `${title} — Location List`;
        }

        if (item?.type === "text") {
            return `${title} — Text Field`;
        }

        return title;
    };
    // ------------------------------------------------------------
    // LOCATIONS EDITOR - UPDATE ONE NESTED TREE NODE
    // Recursively walks the Location Information structure and
    // returns a NEW copy with only the matching node changed.
    //
    // This avoids directly mutating React state and lets us safely
    // edit deeply nested items like Civic Center, C-Line, stations,
    // or other choices.
    //
    // LOCAL STATE ONLY.
    // ------------------------------------------------------------
    const updateLocationNodeById = (items, targetId, updater) => {
        if (!Array.isArray(items)) return items;

        return items.map((item) => {
            const itemId = item?._id ? String(item._id) : null;

            // Found the node we want to update.
            if (itemId && itemId === String(targetId)) {
                return updater(item);
            }

            // If this node has children, recursively search them.
            if (Array.isArray(item?.items) && item.items.length > 0) {
                return {
                    ...item,
                    items: updateLocationNodeById(
                        item.items,
                        targetId,
                        updater
                    ),
                };
            }

            // No match and no nested changes.
            return item;
        });
    };

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - REMOVE ONE NESTED TREE NODE
    // Recursively walks the Location Information structure and
    // returns a NEW copy with the matching node removed.
    //
    // Because child items live inside the selected Location Choice,
    // removing the parent automatically removes its entire branch.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const removeLocationNodeById = (items, targetId) => {
        if (!Array.isArray(items)) return items;

        return items
            .filter((item) => {
                const itemId = item?._id ? String(item._id) : null;

                return !(
                    itemId &&
                    itemId === String(targetId)
                );
            })
            .map((item) => {
                if (Array.isArray(item?.items) && item.items.length > 0) {
                    return {
                        ...item,
                        items: removeLocationNodeById(
                            item.items,
                            targetId
                        ),
                    };
                }

                return item;
            });
    };
    // ------------------------------------------------------------
    // LOCATIONS EDITOR - START EDITING LOCATION CHOICE NAME
    // Copies the selected Location Choice's current English name
    // into the temporary draft field.
    //
    // LOCAL STATE ONLY.
    // ------------------------------------------------------------
    const startEditName = () => {
        if (!selectedNode || selectedNode?.type !== "list_option") {
            return;
        }

        setDraftName(selectedNode?.title?.en_US || "");
        setEditingName(true);
    };


    // ------------------------------------------------------------
    // LOCATIONS EDITOR - CANCEL EDITING LOCATION CHOICE NAME
    // Discards the temporary draft and leaves the tree unchanged.
    // ------------------------------------------------------------
    const cancelEditName = () => {
        setEditingName(false);
        setDraftName("");
    };


    // ------------------------------------------------------------
    // LOCATIONS EDITOR - ACCEPT EDITED LOCATION CHOICE NAME
    // Updates the selected node in the LOCAL Location Information
    // tree only. Nothing is written to Mongo.
    //
    // Also updates selectedNode so the right-side panel immediately
    // displays the new name.
    // ------------------------------------------------------------
    const acceptEditName = () => {
        if (!selectedNode?._id) return;

        const newName = draftName.trim();

        if (!newName) return;

        const updatedSelectedNode = {
            ...selectedNode,
            title: {
                ...(selectedNode?.title || {}),
                en_US: newName,
            },
        };

        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        title: {
                            ...(item?.title || {}),
                            en_US: newName,
                        },
                    })
                ),
            };
        });

        // Keep the right-side Selected Item panel synchronized
        // with the newly updated tree value.
        setSelectedNode(updatedSelectedNode);

        setEditingName(false);
        setDraftName("");
    };

    // ------------------------------------------------------------
    // TEXT ENTRY - START EDITING
    // Copies the selected Text Entry's current English title and
    // prompt/placeholder into temporary draft fields.
    //
    // LOCAL STATE ONLY.
    // ------------------------------------------------------------
    const startEditText = () => {
        if (!selectedNode || selectedNode?.type !== "text") {
            return;
        }

        setDraftTextTitle(
            selectedNode?.title?.en_US || ""
        );

        setDraftTextPrompt(
            selectedNode?.placeholder?.en_US || ""
        );

        setEditingText(true);
    };


    // ------------------------------------------------------------
    // TEXT ENTRY - CANCEL EDITING
    // Discards the temporary text-entry drafts and leaves the
    // working tree unchanged.
    // ------------------------------------------------------------
    const cancelEditText = () => {
        setEditingText(false);
        setDraftTextTitle("");
        setDraftTextPrompt("");
    };


    // ------------------------------------------------------------
    // TEXT ENTRY - ACCEPT EDIT
    // Updates the selected Text Entry's English title and prompt
    // in the LOCAL working tree only.
    //
    // Existing translations and other properties are preserved.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const acceptEditText = () => {
        if (!selectedNode?._id || selectedNode?.type !== "text") {
            return;
        }

        const newTitle = draftTextTitle.trim();
        const newPrompt = draftTextPrompt.trim();

        if (!newTitle) return;

        const updatedSelectedNode = {
            ...selectedNode,

            title: {
                ...(selectedNode?.title || {}),
                en_US: newTitle,
            },

            placeholder: {
                ...(selectedNode?.placeholder || {}),
                en_US: newPrompt,
            },
        };

        // --------------------------------------------------------
        // UPDATE THE WORKING LOCATION TREE
        // --------------------------------------------------------
        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,

                        title: {
                            ...(item?.title || {}),
                            en_US: newTitle,
                        },

                        placeholder: {
                            ...(item?.placeholder || {}),
                            en_US: newPrompt,
                        },
                    })
                ),
            };
        });

        // Keep the right-side Selected Item panel synchronized.
        setSelectedNode(updatedSelectedNode);

        setEditingText(false);
        setDraftTextTitle("");
        setDraftTextPrompt("");
    };

    // ------------------------------------------------------------
    // SELECTION LIST - START EDITING LIST / QUESTION TITLE
    // Copies the selected Selection List's current English title
    // into the temporary draft field.
    //
    // Used for both:
    // - Edit List Name
    // - Edit Question
    //
    // LOCAL STATE ONLY.
    // ------------------------------------------------------------
    const startEditListName = () => {
        if (!selectedNode || selectedNode?.type !== "list") {
            return;
        }

        setDraftListName(selectedNode?.title?.en_US || "");
        setEditingListName(true);
    };


    // ------------------------------------------------------------
    // SELECTION LIST - CANCEL EDITING LIST / QUESTION TITLE
    // Discards the temporary draft and leaves the tree unchanged.
    // ------------------------------------------------------------
    const cancelEditListName = () => {
        setEditingListName(false);
        setDraftListName("");
    };


    // ------------------------------------------------------------
    // SELECTION LIST - ACCEPT EDITED LIST / QUESTION TITLE
    // Updates the selected Selection List in the LOCAL working
    // tree only.
    //
    // This handles both normal list names and Yes/No questions.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const acceptEditListName = () => {
        if (!selectedNode?._id || selectedNode?.type !== "list") {
            return;
        }

        const newName = draftListName.trim();

        if (!newName) return;

        const updatedSelectedNode = {
            ...selectedNode,
            title: {
                ...(selectedNode?.title || {}),
                en_US: newName,
            },
        };

        // --------------------------------------------------------
        // UPDATE THE WORKING LOCATION TREE
        // --------------------------------------------------------
        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        title: {
                            ...(item?.title || {}),
                            en_US: newName,
                        },
                    })
                ),
            };
        });

        // Keep the Selected Item panel synchronized with the tree.
        setSelectedNode(updatedSelectedNode);

        setEditingListName(false);
        setDraftListName("");
    };

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - TOGGLE REQUIRED
    // Toggles the required setting on the currently selected
    // Selection List or Text Entry.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const toggleRequired = () => {
        if (
            !selectedNode?._id ||
            !["list", "text"].includes(selectedNode?.type)
        ) {
            return;
        }

        const newRequiredValue = !Boolean(selectedNode?.required);

        const updatedSelectedNode = {
            ...selectedNode,
            required: newRequiredValue,
        };

        // --------------------------------------------------------
        // UPDATE THE WORKING LOCATION TREE
        // --------------------------------------------------------
        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        required: newRequiredValue,
                    })
                ),
            };
        });

        // Keep the right-side Selected Item panel synchronized.
        setSelectedNode(updatedSelectedNode);
    };

    // ------------------------------------------------------------
    // LOCATION CHOICE - CONFIRM LOCAL REMOVE
    // Removes the selected Location Choice and its entire nested
    // branch from the working React copy.
    //
    // LOCAL STATE ONLY.
    // Refreshing the page reloads the original Mongo configuration.
    // ------------------------------------------------------------
    const confirmRemoveLocationChoice = () => {
        if (!selectedNode?._id) return;

        const targetId = selectedNode._id;

        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: removeLocationNodeById(
                    current.items,
                    targetId
                ),
            };
        });

        // Clear the right-side selection because that node no longer
        // exists in the local working tree.
        setSelectedNode(null);

        setRemovingLocationChoice(false);

        // Also clear any edit state tied to the removed node.
        setEditingName(false);
        setDraftName("");
    };


    // ------------------------------------------------------------
    // YES / NO QUESTION - CONFIRM LOCAL REMOVE
    // Removes the selected Yes/No Question as one complete branch,
    // including both answers and anything nested beneath them.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const confirmRemoveYesNoQuestion = () => {
        if (!selectedNode?._id || !isYesNoList(selectedNode)) {
            return;
        }

        const targetId = selectedNode._id;

        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: removeLocationNodeById(
                    current.items,
                    targetId
                ),
            };
        });

        // The selected question no longer exists in the working tree.
        setSelectedNode(null);

        // Close the confirmation panel.
        setRemovingYesNoQuestion(false);

        // Clear any editing/building state that could still be tied
        // to the removed question.
        setAddingListChoice(false);
        setNewListChoiceName("");

        setAddingYesNoQuestion(false);
        setNewYesNoQuestion("");
        setNewYesChoice("");
        setNewNoChoice("");
    };

    // ------------------------------------------------------------
    // TEXT ENTRY - CONFIRM LOCAL REMOVE
    // Removes the selected Text Entry from the working location
    // tree.
    //
    // Example:
    // Civic Center
    //   -> Describe Location
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const confirmRemoveTextEntry = () => {
        if (!selectedNode?._id || selectedNode?.type !== "text") {
            return;
        }

        const targetId = selectedNode._id;

        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: removeLocationNodeById(
                    current.items,
                    targetId
                ),
            };
        });

        // The removed Text Entry no longer exists in the tree.
        setSelectedNode(null);

        // Close the confirmation panel.
        setRemovingTextEntry(false);

        // Clear any Text Entry edit state tied to the removed node.
        setEditingText(false);
        setDraftTextTitle("");
        setDraftTextPrompt("");
    };

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - START ADDING TOP-LEVEL LOCATION
    // Opens the local Add Location form.
    // ------------------------------------------------------------
    const startAddLocationChoice = () => {
        setNewLocationName("");
        setAddingLocationChoice(true);
    };


    // ------------------------------------------------------------
    // LOCATIONS EDITOR - CANCEL ADDING TOP-LEVEL LOCATION
    // Closes the Add Location form without changing the tree.
    // ------------------------------------------------------------
    const cancelAddLocationChoice = () => {
        setAddingLocationChoice(false);
        setNewLocationName("");
    };

    // ------------------------------------------------------------
    // SELECTION LIST - START ADDING NEW CHOICE
    // Opens the local Add Choice builder beneath the currently
    // selected Selection List.
    //
    // Example:
    // Area
    //   -> East Annex
    //
    // LOCAL STATE ONLY.
    // ------------------------------------------------------------
    const startAddListChoice = () => {
        setNewListChoiceName("");
        setAddingListChoice(true);
    };


    // ------------------------------------------------------------
    // SELECTION LIST - CANCEL ADDING NEW CHOICE
    // Closes the Add Choice builder without changing the tree.
    // ------------------------------------------------------------
    const cancelAddListChoice = () => {
        setAddingListChoice(false);
        setNewListChoiceName("");
    };

    // ------------------------------------------------------------
    // LOCATION CHOICE - START ADDING YES / NO QUESTION
    // Opens the local Yes/No question builder beneath the
    // currently selected Location Choice.
    //
    // LOCAL STATE ONLY.
    // ------------------------------------------------------------
    const startAddYesNoQuestion = () => {
        setNewYesNoQuestion("");
        setNewYesChoice("");
        setNewNoChoice("");
        setAddingYesNoQuestion(true);
    };


    // ------------------------------------------------------------
    // LOCATION CHOICE - CANCEL ADDING YES / NO QUESTION
    // Closes the builder without changing the location tree.
    // ------------------------------------------------------------
    const cancelAddYesNoQuestion = () => {
        setAddingYesNoQuestion(false);
        setNewYesNoQuestion("");
        setNewYesChoice("");
        setNewNoChoice("");
    };

    // ------------------------------------------------------------
    // LOCATION CHOICE - ACCEPT NEW YES / NO QUESTION
    // Creates a new Selection List beneath the currently selected
    // Location Choice, with two Location Choices underneath it:
    // one for Yes and one for No.
    //
    // Example:
    // On a Light Rail
    //   -> Are you in a train car?
    //      -> Yes, in a train car.
    //      -> No, not in a train car.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const acceptAddYesNoQuestion = () => {
        if (!selectedNode?._id || selectedNode?.type !== "list_option") {
            return;
        }

        const question = newYesNoQuestion.trim();
        const yesChoice = newYesChoice.trim();
        const noChoice = newNoChoice.trim();

        if (!question || !yesChoice || !noChoice) {
            return;
        }

        // --------------------------------------------------------
        // NEW YES / NO QUESTION
        // Stored using the same list -> list_option structure
        // already used by existing Transit Watch location logic.
        // --------------------------------------------------------
        const newYesNoList = {
            _id: `local-${Date.now()}`,
            type: "list",
            editor_type: "yes_no_question",

            title: {
                en_US: question,
            },

            required: false,

            items: [
                {
                    _id: `local-${Date.now()}-yes`,
                    type: "list_option",
                    editor_type: "yes_no_answer",

                    title: {
                        en_US: yesChoice,
                    },

                    items: [],
                },
                {
                    _id: `local-${Date.now()}-no`,
                    type: "list_option",
                    editor_type: "yes_no_answer",

                    title: {
                        en_US: noChoice,
                    },

                    items: [],
                },
            ],
        };

        const updatedSelectedNode = {
            ...selectedNode,
            items: [
                ...(Array.isArray(selectedNode?.items)
                    ? selectedNode.items
                    : []),
                newYesNoList,
            ],
        };

        // --------------------------------------------------------
        // UPDATE THE WORKING LOCATION TREE
        // Adds the new Yes/No question beneath the selected
        // Location Choice.
        // --------------------------------------------------------
        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        items: [
                            ...(Array.isArray(item?.items)
                                ? item.items
                                : []),
                            newYesNoList,
                        ],
                    })
                ),
            };
        });

        // Keep the right-side Selected Item panel synchronized.
        setSelectedNode(updatedSelectedNode);

        // Close and reset the Yes/No builder.
        setAddingYesNoQuestion(false);
        setNewYesNoQuestion("");
        setNewYesChoice("");
        setNewNoChoice("");
    };

    // ------------------------------------------------------------
    // SELECTION LIST - ACCEPT NEW CHOICE
    // Adds a new Location Choice beneath the currently selected
    // Selection List.
    //
    // Example:
    // Area
    //   -> East Annex
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const acceptAddListChoice = () => {
        if (!selectedNode?._id || selectedNode?.type !== "list") {
            return;
        }

        const newChoiceName = newListChoiceName.trim();

        if (!newChoiceName) return;

        // --------------------------------------------------------
        // NEW LIST CHOICE
        // Temporary browser-only ID is used until a future Save.
        // --------------------------------------------------------
        const newChoice = {
            _id: `local-${Date.now()}`,
            type: "list_option",

            title: {
                en_US: newChoiceName,
            },

            items: [],
        };

        const updatedSelectedNode = {
            ...selectedNode,
            items: [
                ...(Array.isArray(selectedNode?.items)
                    ? selectedNode.items
                    : []),
                newChoice,
            ],
        };

        // --------------------------------------------------------
        // UPDATE THE WORKING LOCATION TREE
        // Adds the new choice beneath the selected Selection List.
        // --------------------------------------------------------
        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        items: [
                            ...(Array.isArray(item?.items)
                                ? item.items
                                : []),
                            newChoice,
                        ],
                    })
                ),
            };
        });

        // Keep the right-side Selected Item panel synchronized.
        setSelectedNode(updatedSelectedNode);

        // Close and reset the Add Choice builder.
        setAddingListChoice(false);
        setNewListChoiceName("");
    };


    // ------------------------------------------------------------
    // LOCATION CHOICE - START ADDING NESTED LOCATION LIST
    // Opens the local builder for a new location list beneath the
    // currently selected Location Choice.
    //
    // Example:
    // Union Station
    //   -> Area
    //
    // LOCAL STATE ONLY.
    // ------------------------------------------------------------
    const startAddLocationList = () => {
        setNewLocationListName("");
        setAddingLocationList(true);
    };


    // ------------------------------------------------------------
    // LOCATION CHOICE - CANCEL ADDING NESTED LOCATION LIST
    // Closes the builder without changing the location tree.
    // ------------------------------------------------------------
    const cancelAddLocationList = () => {
        setAddingLocationList(false);
        setNewLocationListName("");
    };

    // ------------------------------------------------------------
    // LOCATION CHOICE - ACCEPT NEW NESTED LOCATION LIST
    // Creates a new Selection List beneath the currently selected
    // Location Choice.
    //
    // Example:
    // Union Station
    //   -> Area
    //
    // The list starts empty. We will add its choices in the next
    // step after this creation flow is proven.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const acceptAddLocationList = () => {
        if (!selectedNode?._id || selectedNode?.type !== "list_option") {
            return;
        }

        const newListName = newLocationListName.trim();

        if (!newListName) return;

        // --------------------------------------------------------
        // NEW NESTED LOCATION LIST
        // Temporary browser-only ID is used until a future Save.
        // --------------------------------------------------------
        const newLocationList = {
            _id: `local-${Date.now()}`,
            type: "list",

            title: {
                en_US: newListName,
            },

            required: false,

            items: [],
        };

        const updatedSelectedNode = {
            ...selectedNode,
            items: [
                ...(Array.isArray(selectedNode?.items)
                    ? selectedNode.items
                    : []),
                newLocationList,
            ],
        };

        // --------------------------------------------------------
        // UPDATE THE WORKING LOCATION TREE
        // Adds the new list beneath the selected Location Choice.
        // --------------------------------------------------------
        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        items: [
                            ...(Array.isArray(item?.items)
                                ? item.items
                                : []),
                            newLocationList,
                        ],
                    })
                ),
            };
        });

        // Keep the right-side Selected Item panel synchronized.
        setSelectedNode(updatedSelectedNode);

        // Close and reset the temporary builder state.
        setAddingLocationList(false);
        setNewLocationListName("");
    };

    // ------------------------------------------------------------
    // LOCATIONS EDITOR - ACCEPT NEW TOP-LEVEL LOCATION
    // Adds a new Location Choice beneath the selected top-level
    // location selection list.
    //
    // The list is identified by its position in the Location
    // Information schema, NOT by its displayed title.
    //
    // LOCAL STATE ONLY.
    // A page refresh restores the Mongo version.
    // ------------------------------------------------------------
    const acceptAddLocationChoice = () => {
        const newName = newLocationName.trim();

        if (!newName || !selectedNode?._id) return;

        const newChoice = {
            // Temporary browser-only ID so the new node can be
            // selected and manipulated before a future Mongo save.
            _id: `local-${Date.now()}`,

            type: "list_option",

            title: {
                en_US: newName,
            },

            items: [],
        };

        const updatedSelectedNode = {
            ...selectedNode,
            items: [
                ...(Array.isArray(selectedNode?.items)
                    ? selectedNode.items
                    : []),
                newChoice,
            ],
        };

        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        items: [
                            ...(Array.isArray(item?.items)
                                ? item.items
                                : []),
                            newChoice,
                        ],
                    })
                ),
            };
        });

        // Keep the right-side panel synchronized with the updated list.
        setSelectedNode(updatedSelectedNode);

        setAddingLocationChoice(false);
        setNewLocationName("");
    };


    // ------------------------------------------------------------
    // LOCATIONS EDITOR - IDENTIFY TOP-LEVEL LOCATION LIST
    // Determines whether the selected node is one of the direct
    // children of the Location Information section.
    //
    // This is based on schema structure, NOT on displayed text such
    // as "Select Incident Location".
    // ------------------------------------------------------------
    const isTopLevelLocationList = (node) => {
        if (!node || node?.type !== "list") return false;

        return Array.isArray(locationSection?.items) &&
            locationSection.items.some((item) => {
                if (item?._id && node?._id) {
                    return String(item._id) === String(node._id);
                }

                return item === node;
            });
    };

    // ------------------------------------------------------------
    // LOCATION CHOICE - ADD DESCRIBE LOCATION TEXT
    // Adds the standard "Describe Location" text-entry field beneath
    // the currently selected Location Choice.
    //
    // LOCAL STATE ONLY.
    // Nothing is written to Mongo.
    // ------------------------------------------------------------
    const addDescribeLocation = () => {
        if (!selectedNode?._id || selectedNode?.type !== "list_option") {
            return;
        }

        // Do not allow a duplicate Describe Location field.
        const alreadyExists = Array.isArray(selectedNode?.items) &&
            selectedNode.items.some(
                (child) =>
                    child?.type === "text" &&
                    String(child?.title?.en_US || "")
                        .trim()
                        .toLowerCase() === "describe location"
            );

        if (alreadyExists) return;

        // Matches the existing Transit Watch Describe Location
        // text-field structure used in the report schema.
        const newDescribeLocation = {
            _id: `local-${Date.now()}`,
            type: "text",

            title: {
                en_US: "Describe Location",
                es_US: "Describir la Locación",
            },

            required: false,

            placeholder: {
                en_US: "Please describe where the incident happened",
                es_US: "Por favor, describa dónde ocurrió el incidente",
            },
        };

        const updatedSelectedNode = {
            ...selectedNode,
            items: [
                ...(Array.isArray(selectedNode?.items)
                    ? selectedNode.items
                    : []),
                newDescribeLocation,
            ],
        };

        // Update the selected Location Choice inside the full
        // nested working tree.
        setLocationSection((current) => {
            if (!current) return current;

            return {
                ...current,
                items: updateLocationNodeById(
                    current.items,
                    selectedNode._id,
                    (item) => ({
                        ...item,
                        items: [
                            ...(Array.isArray(item?.items)
                                ? item.items
                                : []),
                            newDescribeLocation,
                        ],
                    })
                ),
            };
        });

        // Keep the right-side Selected Item panel synchronized.
        setSelectedNode(updatedSelectedNode);
    };

    /////////////////////////////////////////////////
    ////////////////////////////////////////////////

    // ------------------------------------------------------------
    // THIS IS THE RETURN BLOCK OF JSX CODE
    // before it is the javascript code with functions and var 
    // this JSX code makes the browser page
    // ------------------------------------------------------------
    return (
        <div style={{ padding: 12 }}>
            <h4>Locations Editor</h4>

            <div>
                Agency ID: {agencyId || "Not loaded"}
            </div>

            <div>
                Incident Report Schema ID:{" "}
                {reportSchemaId || "Not found"}
            </div>

            {/* ------------------------------------------------------------ */}
            {/* LOCATIONS EDITOR - LOAD STATUS                              */}
            {/* Temporary diagnostic display before we build the tree UI.  */}
            {/* ------------------------------------------------------------ */}

            {loading && (
                <div style={{ marginTop: 16 }}>
                    Loading Location Information...
                </div>
            )}

            {loadError && (
                <div
                    style={{
                        marginTop: 16,
                        color: "#dc3545",
                    }}
                >
                    {loadError}
                </div>
            )}

            {/* ------------------------------------------------------------ */}
            {/* LOCATIONS EDITOR - TREE + SELECTED ITEM PANEL               */}
            {/* Left side: collapsible location structure.                  */}
            {/* Right side: details for the currently selected tree node.   */}
            {/* READ ONLY for now.                                          */}
            {/* ------------------------------------------------------------ */}

            {!loading && !loadError && locationSection && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 12 }}>
                        <strong>
                            {locationSection?.title?.en_US || "Location Information"}
                        </strong>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1.5fr) minmax(320px, 1fr)",
                            gap: 24,
                            alignItems: "start",
                        }}
                    >
                        {/* -------------------------------------------------------- */}
                        {/* LOCATIONS EDITOR - LEFT TREE                            */}
                        {/* -------------------------------------------------------- */}
                        <div>
                            {Array.isArray(locationSection?.items) &&
                                locationSection.items.map((item, index) => (
                                    <LocationTreeNode
                                        key={item?._id || `location-root-${index}`}
                                        item={item}
                                        depth={0}
                                        selectedNode={selectedNode}
                                        onSelect={setSelectedNode}
                                    />
                                ))}
                        </div>

                        {/* -------------------------------------------------------- */}
                        {/* LOCATIONS EDITOR - RIGHT SIDE SELECTED ITEM DETAILS      */}
                        {/* This becomes the contextual editor later.               */}
                        {/* -------------------------------------------------------- */}
                        <div
                            style={{
                                border: "1px solid #d9d9d9",
                                borderRadius: 8,
                                padding: 18,
                                background: "#fff",
                                position: "sticky",
                                top: 12,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "#6c757d",
                                    textTransform: "uppercase",
                                    letterSpacing: ".4px",
                                    marginBottom: 10,
                                }}
                            >
                                Selected Item
                            </div>

                            {!selectedNode ? (
                                <div style={{ color: "#6c757d" }}>
                                    Select an item from the location tree to view its details.
                                </div>
                            ) : (
                                <>
                                    <h5 style={{ marginBottom: 16 }}>
                                        {selectedNode?.title?.en_US || "Untitled item"}
                                    </h5>

                                    <div style={{ marginBottom: 8 }}>
                                        <strong>Type:</strong>{" "}
                                        {selectedNode?.type === "list"
                                            ? "Selection List"
                                            : selectedNode?.type === "list_option"
                                                ? "Location Choice"
                                                : selectedNode?.type === "text"
                                                    ? "Text Entry"
                                                    : selectedNode?.type || "Unknown"}
                                    </div>

                                    {"required" in selectedNode && (
                                        <div style={{ marginBottom: 8 }}>
                                            <strong>Required:</strong>{" "}
                                            {selectedNode.required ? "Yes" : "No"}
                                        </div>
                                    )}

                                    {Array.isArray(selectedNode?.items) && (
                                        <div style={{ marginBottom: 8 }}>
                                            <strong>Contains:</strong>{" "}
                                            {selectedNode.items.length}{" "}
                                            {selectedNode.items.length === 1
                                                ? "child item"
                                                : "child items"}
                                        </div>
                                    )}

                                    {selectedNode?.placeholder?.en_US && (
                                        <div style={{ marginBottom: 8 }}>
                                            <strong>Prompt:</strong>{" "}
                                            {selectedNode.placeholder.en_US}
                                        </div>
                                    )}

                                    {selectedNode?.subtitle?.en_US && (
                                        <div style={{ marginBottom: 8 }}>
                                            <strong>Help Text:</strong>{" "}
                                            {selectedNode.subtitle.en_US}
                                        </div>
                                    )}

                                    {/* -------------------------------------------------- */}
                                    {/* SELECTED ITEM - AVAILABLE ACTIONS                  */}
                                    {/* Shows context-aware actions for the selected node. */}
                                    {/* DISPLAY ONLY FOR NOW - buttons are disabled.       */}
                                    {/* -------------------------------------------------- */}
                                    <div
                                        style={{
                                            marginTop: 18,
                                            paddingTop: 14,
                                            borderTop: "1px solid #eee",
                                        }}
                                    >
                                        <div
                                            style={{
                                                marginBottom: 10,
                                                fontWeight: 600,
                                            }}
                                        >
                                            Available Actions
                                        </div>

                                        <div
                                            style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 8,
                                            }}
                                        >
                                            {/* ------------------------------------------------ */}
                                            {/* ACTIONS FOR A SELECTION LIST                     */}
                                            {/* Example: Rail Line, Nearby Station               */}
                                            {/* DISPLAY ONLY FOR NOW - controls are disabled.    */}
                                            {/* ------------------------------------------------ */}
                                            {selectedNode?.type === "list" && (
                                                <div style={{ width: "100%" }}>

                                                    {/* ------------------------------------------------ */}
                                                    {/* SELECTION LIST - STANDARD ACTIONS                */}
                                                    {/* Top-level location list uses "Add Location".     */}
                                                    {/* Nested lists continue to use "Add Choice".       */}
                                                    {/* ------------------------------------------------ */}
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 8,
                                                            marginBottom: addingLocationChoice ? 14 : 0,
                                                        }}
                                                    >
                                                        {/* ------------------------------------------------ */}
                                                        {/* SELECTION LIST - EDIT NAME / QUESTION            */}
                                                        {/* Normal lists edit the list name.                 */}
                                                        {/* Yes/No lists edit the question text.             */}
                                                        {/* LOCAL STATE ONLY.                                */}
                                                        {/* ------------------------------------------------ */}
                                                        {editingListName ? (
                                                            <div
                                                                style={{
                                                                    width: "100%",
                                                                    marginBottom: 12,
                                                                }}
                                                            >
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={draftListName}
                                                                    onChange={(e) => setDraftListName(e.target.value)}
                                                                    style={{
                                                                        border: "2px solid #0d6efd",
                                                                        background: "#fffdf2",
                                                                        boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                                                                        marginBottom: 10,
                                                                    }}
                                                                />

                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        gap: 8,
                                                                    }}
                                                                >
                                                                    {/* -------------------------------------------- */}
                                                                    {/* ACCEPT LIST / QUESTION TITLE EDIT            */}
                                                                    {/* -------------------------------------------- */}
                                                                    <button
                                                                        type="button"
                                                                        className="cfg-icon-btn"
                                                                        title="Save edit"
                                                                        onClick={acceptEditListName}
                                                                        disabled={!draftListName.trim()}
                                                                    >
                                                                        <span
                                                                            className="material-icons cfg-icon"
                                                                            style={{ color: "#198754" }}
                                                                        >
                                                                            check
                                                                        </span>
                                                                    </button>

                                                                    {/* -------------------------------------------- */}
                                                                    {/* CANCEL LIST / QUESTION TITLE EDIT            */}
                                                                    {/* -------------------------------------------- */}
                                                                    <button
                                                                        type="button"
                                                                        className="cfg-icon-btn"
                                                                        title="Cancel"
                                                                        onClick={cancelEditListName}
                                                                    >
                                                                        <span className="material-icons cfg-icon cfg-close-icon">
                                                                            close
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                onClick={startEditListName}
                                                            >
                                                                {isYesNoList(selectedNode)
                                                                    ? "Edit Question"
                                                                    : "Edit List Name"}
                                                            </button>
                                                        )}

                                                        {/* ------------------------------------------------ */}
                                                        {/* SELECTION LIST - ADD LOCATION OR ADD CHOICE      */}
                                                        {/* Top-level list adds Locations. Nested lists add  */}
                                                        {/* Location Choices beneath the selected list.      */}
                                                        {/* ------------------------------------------------ */}

                                                        {/* ------------------------------------------------ */}
                                                        {/* ADD LOCATION / LIST CHOICE                      */}
                                                        {/* Yes/No answers remain a fixed pair, so a        */}
                                                        {/* Yes/No Question cannot receive a third choice.  */}
                                                        {/* ------------------------------------------------ */}
                                                        {!isYesNoList(selectedNode) && (
                                                            <>
                                                                {isTopLevelLocationList(selectedNode) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary"
                                                                        onClick={startAddLocationChoice}
                                                                        disabled={addingLocationChoice}
                                                                    >
                                                                        + Add Location
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary"
                                                                        onClick={startAddListChoice}
                                                                        disabled={addingListChoice}
                                                                    >
                                                                        + Add Choice
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* ------------------------------------------------ */}
                                                        {/* SELECTION LIST - TOGGLE REQUIRED                 */}
                                                        {/* Updates the Required setting in local state only.*/}
                                                        {/* ------------------------------------------------ */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            onClick={toggleRequired}
                                                        >
                                                            {selectedNode?.required
                                                                ? "Make Optional"
                                                                : "Make Required"}
                                                        </button>

                                                        {/* ------------------------------------------------ */}
                                                        {/* REMOVE YES / NO QUESTION AS ONE PAIRED UNIT      */}
                                                        {/* Individual Yes/No answers cannot be removed.     */}
                                                        {/* ------------------------------------------------ */}
                                                        {isYesNoList(selectedNode) && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger"
                                                                onClick={() => setRemovingYesNoQuestion(true)}
                                                            >
                                                                Remove Yes/No Question
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* ------------------------------------------------ */}
                                                    {/* YES / NO QUESTION - REMOVE CONFIRMATION          */}
                                                    {/* Removes the entire question, both answers, and   */}
                                                    {/* anything configured beneath either answer.       */}
                                                    {/* LOCAL STATE ONLY.                                */}
                                                    {/* ------------------------------------------------ */}
                                                    {isYesNoList(selectedNode) &&
                                                        removingYesNoQuestion && (
                                                            <div
                                                                style={{
                                                                    marginTop: 14,
                                                                    padding: 14,
                                                                    border: "1px solid #dc3545",
                                                                    borderRadius: 6,
                                                                    background: "#fff8f8",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        marginBottom: 8,
                                                                    }}
                                                                >
                                                                    Remove "{selectedNode?.title?.en_US || "this Yes/No question"}"?
                                                                </div>

                                                                <div style={{ marginBottom: 8 }}>
                                                                    This will also remove both answers and anything configured beneath them:
                                                                </div>

                                                                {Array.isArray(selectedNode?.items) &&
                                                                    selectedNode.items.map((answer, index) => (
                                                                        <div
                                                                            key={answer?._id || `yes-no-answer-${index}`}
                                                                            style={{
                                                                                marginLeft: 8,
                                                                                marginBottom: 4,
                                                                            }}
                                                                        >
                                                                            ✓ {answer?.title?.en_US || "Untitled answer"}
                                                                        </div>
                                                                    ))}

                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        gap: 8,
                                                                        marginTop: 14,
                                                                    }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary"
                                                                        onClick={() => setRemovingYesNoQuestion(false)}
                                                                    >
                                                                        Cancel
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger"
                                                                        onClick={confirmRemoveYesNoQuestion}
                                                                    >
                                                                        Remove Yes/No Question
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    {/* ------------------------------------------------ */}
                                                    {/* NESTED SELECTION LIST - ADD CHOICE FORM          */}
                                                    {/* Adds a new Location Choice beneath the selected  */}
                                                    {/* nested Selection List.                           */}
                                                    {/* LOCAL STATE ONLY.                                */}
                                                    {/* ------------------------------------------------ */}
                                                    {!isTopLevelLocationList(selectedNode) &&
                                                        addingListChoice && (
                                                            <div
                                                                style={{
                                                                    marginTop: 14,
                                                                    padding: 14,
                                                                    border: "1px solid #ddd",
                                                                    borderRadius: 6,
                                                                    background: "#fafafa",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        marginBottom: 8,
                                                                    }}
                                                                >
                                                                    New Choice
                                                                </div>

                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Enter choice name..."
                                                                    value={newListChoiceName}
                                                                    onChange={(e) =>
                                                                        setNewListChoiceName(e.target.value)
                                                                    }
                                                                    style={{
                                                                        border: "2px solid #0d6efd",
                                                                        background: "#fffdf2",
                                                                        boxShadow:
                                                                            "0 0 0 2px rgba(13,110,253,.12)",
                                                                        marginBottom: 10,
                                                                    }}
                                                                />

                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        gap: 8,
                                                                    }}
                                                                >
                                                                    {/* -------------------------------------------- */}
                                                                    {/* ACCEPT NEW LIST CHOICE                       */}
                                                                    {/* -------------------------------------------- */}
                                                                    <button
                                                                        type="button"
                                                                        className="cfg-icon-btn"
                                                                        title="Add choice"
                                                                        onClick={acceptAddListChoice}
                                                                        disabled={!newListChoiceName.trim()}
                                                                    >
                                                                        <span
                                                                            className="material-icons cfg-icon"
                                                                            style={{ color: "#198754" }}
                                                                        >
                                                                            check
                                                                        </span>
                                                                    </button>

                                                                    {/* -------------------------------------------- */}
                                                                    {/* CANCEL NEW LIST CHOICE                       */}
                                                                    {/* -------------------------------------------- */}
                                                                    <button
                                                                        type="button"
                                                                        className="cfg-icon-btn"
                                                                        title="Cancel"
                                                                        onClick={cancelAddListChoice}
                                                                    >
                                                                        <span className="material-icons cfg-icon cfg-close-icon">
                                                                            close
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                    {/* ------------------------------------------------ */}
                                                    {/* TOP-LEVEL LOCATION LIST - ADD LOCATION FORM      */}
                                                    {/* Creates a new Location Choice in local state.    */}
                                                    {/* Nothing is written to Mongo.                     */}
                                                    {/* ------------------------------------------------ */}
                                                    {isTopLevelLocationList(selectedNode) &&
                                                        addingLocationChoice && (
                                                            <div
                                                                style={{
                                                                    padding: 14,
                                                                    border: "1px solid #ddd",
                                                                    borderRadius: 6,
                                                                    background: "#fafafa",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        marginBottom: 10,
                                                                    }}
                                                                >
                                                                    New Location
                                                                </div>

                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Enter location name..."
                                                                    value={newLocationName}
                                                                    onChange={(e) =>
                                                                        setNewLocationName(e.target.value)
                                                                    }
                                                                    style={{
                                                                        border: "2px solid #0d6efd",
                                                                        background: "#fffdf2",
                                                                        boxShadow:
                                                                            "0 0 0 2px rgba(13,110,253,.12)",
                                                                        marginBottom: 10,
                                                                    }}
                                                                />

                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        gap: 8,
                                                                    }}
                                                                >
                                                                    {/* -------------------------------------------- */}
                                                                    {/* ACCEPT NEW TOP-LEVEL LOCATION                */}
                                                                    {/* -------------------------------------------- */}
                                                                    <button
                                                                        type="button"
                                                                        className="cfg-icon-btn"
                                                                        title="Add location"
                                                                        onClick={acceptAddLocationChoice}
                                                                        disabled={!newLocationName.trim()}
                                                                    >
                                                                        <span
                                                                            className="material-icons cfg-icon"
                                                                            style={{ color: "#198754" }}
                                                                        >
                                                                            check
                                                                        </span>
                                                                    </button>

                                                                    {/* -------------------------------------------- */}
                                                                    {/* CANCEL NEW TOP-LEVEL LOCATION                */}
                                                                    {/* -------------------------------------------- */}
                                                                    <button
                                                                        type="button"
                                                                        className="cfg-icon-btn"
                                                                        title="Cancel"
                                                                        onClick={cancelAddLocationChoice}
                                                                    >
                                                                        <span className="material-icons cfg-icon cfg-close-icon">
                                                                            close
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>
                                            )}

                                            {/* ------------------------------------------------ */}
                                            {/* ACTIONS FOR A LOCATION CHOICE                   */}
                                            {/* Example: Civic Center, C-Line, On a Bus         */}
                                            {/* Shows what is already configured beneath the    */}
                                            {/* location and possible next-step building blocks.*/}
                                            {/* DISPLAY ONLY FOR NOW.                           */}
                                            {/* ------------------------------------------------ */}
                                            {selectedNode?.type === "list_option" && (
                                                <div style={{ width: "100%" }}>

                                                    {/* ------------------------------------------------ */}
                                                    {/* LOCATION CHOICE - EDIT NAME CONTROLS            */}
                                                    {/* Allows the selected Location Choice name to be  */}
                                                    {/* edited in local React state only.               */}
                                                    {/* Nothing is written to Mongo.                    */}
                                                    {/* ------------------------------------------------ */}
                                                    {editingName ? (
                                                        <div
                                                            style={{
                                                                marginBottom: 18,
                                                            }}
                                                        >
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={draftName}
                                                                onChange={(e) => setDraftName(e.target.value)}
                                                                style={{
                                                                    border: "2px solid #0d6efd",
                                                                    background: "#fffdf2",
                                                                    boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                                                                    marginBottom: 10,
                                                                }}
                                                            />

                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    gap: 8,
                                                                }}
                                                            >
                                                                {/* -------------------------------------------- */}
                                                                {/* ACCEPT LOCATION CHOICE NAME EDIT             */}
                                                                {/* -------------------------------------------- */}
                                                                <button
                                                                    type="button"
                                                                    className="cfg-icon-btn"
                                                                    title="Save name edit"
                                                                    onClick={acceptEditName}
                                                                    disabled={!draftName.trim()}
                                                                >
                                                                    <span
                                                                        className="material-icons cfg-icon"
                                                                        style={{ color: "#198754" }}
                                                                    >
                                                                        check
                                                                    </span>
                                                                </button>

                                                                {/* -------------------------------------------- */}
                                                                {/* CANCEL LOCATION CHOICE NAME EDIT             */}
                                                                {/* -------------------------------------------- */}
                                                                <button
                                                                    type="button"
                                                                    className="cfg-icon-btn"
                                                                    title="Cancel name edit"
                                                                    onClick={cancelEditName}
                                                                >
                                                                    <span className="material-icons cfg-icon cfg-close-icon">
                                                                        close
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                gap: 8,
                                                                marginBottom: 18,
                                                            }}
                                                        >
                                                            {/* -------------------------------------------- */}
                                                            {/* START EDITING LOCATION CHOICE NAME          */}
                                                            {/* -------------------------------------------- */}
                                                            {/* -------------------------------------------- */}
                                                            {/* EDIT LOCATION CHOICE / YES-NO ANSWER TEXT   */}
                                                            {/* Yes/No answers stay paired, so they use      */}
                                                            {/* answer-specific wording.                     */}
                                                            {/* -------------------------------------------- */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                onClick={startEditName}
                                                            >
                                                                {isYesNoAnswerNode(selectedNode)
                                                                    ? "Edit Answer Text"
                                                                    : "Edit Name"}
                                                            </button>

                                                            {/* -------------------------------------------- */}
                                                            {/* REMOVE NORMAL LOCATION CHOICE ONLY           */}
                                                            {/* Individual Yes/No answers cannot be removed  */}
                                                            {/* because the pair must stay structurally valid.*/}
                                                            {/* -------------------------------------------- */}
                                                            {!isYesNoAnswerNode(selectedNode) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger"
                                                                    onClick={() => setRemovingLocationChoice(true)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}


                                                            {removingLocationChoice && (
                                                                <div
                                                                    style={{
                                                                        width: "100%",
                                                                        marginTop: 14,
                                                                        padding: 14,
                                                                        border: "1px solid #dc3545",
                                                                        borderRadius: 6,
                                                                        background: "#fff8f8",
                                                                    }}
                                                                >
                                                                    <div style={{ fontWeight: 700, marginBottom: 8 }}>
                                                                        Remove "{selectedNode?.title?.en_US || "this location"}"?
                                                                    </div>

                                                                    {Array.isArray(selectedNode?.items) &&
                                                                        selectedNode.items.length > 0 && (
                                                                            <>
                                                                                <div style={{ marginBottom: 6 }}>
                                                                                    This will also remove its configured additional information:
                                                                                </div>

                                                                                {selectedNode.items.map((child, index) => (
                                                                                    <div
                                                                                        key={child?._id || `remove-child-${index}`}
                                                                                        style={{ marginLeft: 8 }}
                                                                                    >
                                                                                        ✓ {child?.title?.en_US || "Untitled item"}
                                                                                    </div>
                                                                                ))}
                                                                            </>
                                                                        )}

                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            gap: 8,
                                                                            marginTop: 14,
                                                                        }}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-secondary"
                                                                            onClick={() => setRemovingLocationChoice(false)}
                                                                        >
                                                                            Cancel
                                                                        </button>

                                                                        {/* ------------------------------------------------ */}
                                                                        {/* CONFIRM LOCAL LOCATION BRANCH REMOVE             */}
                                                                        {/* Removes this location and everything beneath it */}
                                                                        {/* from the working React copy only.               */}
                                                                        {/* ------------------------------------------------ */}
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-danger"
                                                                            onClick={confirmRemoveLocationChoice}
                                                                        >
                                                                            Remove Location
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}


                                                    {/* ------------------------------------------------ */}
                                                    {/* EXISTING ADDITIONAL INFORMATION                 */}
                                                    {/* Explains what the app already asks after this   */}
                                                    {/* location choice is selected.                    */}
                                                    {/* ------------------------------------------------ */}
                                                    {Array.isArray(selectedNode?.items) &&
                                                        selectedNode.items.length > 0 && (
                                                            <div
                                                                style={{
                                                                    paddingTop: 14,
                                                                    borderTop: "1px solid #eee",
                                                                    marginBottom: 18,
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        marginBottom: 10,
                                                                    }}
                                                                >
                                                                    Additional Information already configured:
                                                                </div>

                                                                {selectedNode.items.map((child, index) => (
                                                                    <div
                                                                        key={child?._id || `configured-child-${index}`}
                                                                        style={{
                                                                            marginBottom: 7,
                                                                        }}
                                                                    >
                                                                        <span
                                                                            style={{
                                                                                color: "#198754",
                                                                                fontWeight: 700,
                                                                                marginRight: 8,
                                                                            }}
                                                                        >
                                                                            ✓
                                                                        </span>

                                                                        {describeConfiguredChild(child)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}


                                                    {/* ------------------------------------------------ */}
                                                    {/* ADDITIONAL INFORMATION OPTIONS                  */}
                                                    {/* These are the three guided building blocks we   */}
                                                    {/* currently support conceptually.                 */}
                                                    {/* ------------------------------------------------ */}
                                                    <div
                                                        style={{
                                                            paddingTop: 14,
                                                            borderTop: "1px solid #eee",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                fontWeight: 600,
                                                                marginBottom: 12,
                                                            }}
                                                        >
                                                            Do you need more information for this location?
                                                        </div>

                                                        {/* Do not offer a duplicate Describe Location */}
                                                        {!selectedNode?.items?.some(
                                                            (child) =>
                                                                child?.type === "text" &&
                                                                String(child?.title?.en_US || "")
                                                                    .trim()
                                                                    .toLowerCase() === "describe location"
                                                        ) && (
                                                                <div style={{ marginBottom: 10 }}>
                                                                    <label>
                                                                        {/* ------------------------------------------------ */}
                                                                        {/* ADD STANDARD DESCRIBE LOCATION TEXT FIELD        */}
                                                                        {/* Adds locally beneath the selected location.      */}
                                                                        {/* ------------------------------------------------ */}
                                                                        <input
                                                                            type="radio"
                                                                            name="location-more-info"
                                                                            onChange={addDescribeLocation}
                                                                            style={{ marginRight: 8 }}
                                                                        />
                                                                        Yes — add "Describe Location"
                                                                    </label>
                                                                </div>
                                                            )}

                                                        {/* ------------------------------------------------ */}
                                                        {/* ADD ANOTHER LOCATION LIST                        */}
                                                        {/* Opens the local nested-list builder beneath the  */}
                                                        {/* currently selected Location Choice.              */}
                                                        {/* ------------------------------------------------ */}
                                                        <div style={{ marginBottom: 10 }}>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="location-more-info"
                                                                    checked={addingLocationList}
                                                                    onChange={startAddLocationList}
                                                                    style={{ marginRight: 8 }}
                                                                />
                                                                Yes — add another location list
                                                            </label>

                                                            {/* ------------------------------------------------ */}
                                                            {/* NESTED LOCATION LIST - NAME BUILDER              */}
                                                            {/* Example: "Area", "Nearby Station", "Store"       */}
                                                            {/* LOCAL STATE ONLY.                                */}
                                                            {/* ------------------------------------------------ */}
                                                            {addingLocationList && (
                                                                <div
                                                                    style={{
                                                                        marginTop: 10,
                                                                        padding: 12,
                                                                        border: "1px solid #ddd",
                                                                        borderRadius: 6,
                                                                        background: "#fafafa",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            fontWeight: 600,
                                                                            marginBottom: 8,
                                                                        }}
                                                                    >
                                                                        Location List Name
                                                                    </div>

                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Enter location list name..."
                                                                        value={newLocationListName}
                                                                        onChange={(e) =>
                                                                            setNewLocationListName(e.target.value)
                                                                        }
                                                                        style={{
                                                                            border: "2px solid #0d6efd",
                                                                            background: "#fffdf2",
                                                                            boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />

                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        {/* -------------------------------------------- */}
                                                                        {/* ACCEPT NEW NESTED LOCATION LIST             */}
                                                                        {/* -------------------------------------------- */}
                                                                        <button
                                                                            type="button"
                                                                            className="cfg-icon-btn"
                                                                            title="Add location list"
                                                                            onClick={acceptAddLocationList}
                                                                            disabled={!newLocationListName.trim()}
                                                                        >
                                                                            <span
                                                                                className="material-icons cfg-icon"
                                                                                style={{ color: "#198754" }}
                                                                            >
                                                                                check
                                                                            </span>
                                                                        </button>

                                                                        {/* -------------------------------------------- */}
                                                                        {/* CANCEL NEW NESTED LOCATION LIST             */}
                                                                        {/* -------------------------------------------- */}
                                                                        <button
                                                                            type="button"
                                                                            className="cfg-icon-btn"
                                                                            title="Cancel"
                                                                            onClick={cancelAddLocationList}
                                                                        >
                                                                            <span className="material-icons cfg-icon cfg-close-icon">
                                                                                close
                                                                            </span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* ------------------------------------------------ */}
                                                        {/* ADD YES / NO QUESTION                            */}
                                                        {/* Opens a local builder beneath the selected       */}
                                                        {/* Location Choice.                                 */}
                                                        {/* ------------------------------------------------ */}
                                                        <div style={{ marginBottom: 10 }}>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="location-more-info"
                                                                    checked={addingYesNoQuestion}
                                                                    onChange={startAddYesNoQuestion}
                                                                    style={{ marginRight: 8 }}
                                                                />
                                                                Yes — add a Yes/No question
                                                            </label>

                                                            {/* ------------------------------------------------ */}
                                                            {/* YES / NO QUESTION BUILDER                        */}
                                                            {/* Creates one Selection List with Yes and No       */}
                                                            {/* Location Choices beneath it.                     */}
                                                            {/* LOCAL STATE ONLY.                                */}
                                                            {/* ------------------------------------------------ */}
                                                            {addingYesNoQuestion && (
                                                                <div
                                                                    style={{
                                                                        marginTop: 10,
                                                                        padding: 12,
                                                                        border: "1px solid #ddd",
                                                                        borderRadius: 6,
                                                                        background: "#fafafa",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            fontWeight: 600,
                                                                            marginBottom: 8,
                                                                        }}
                                                                    >
                                                                        Yes / No Question
                                                                    </div>

                                                                    {/* -------------------------------------------- */}
                                                                    {/* QUESTION TEXT                                */}
                                                                    {/* -------------------------------------------- */}
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Enter question..."
                                                                        value={newYesNoQuestion}
                                                                        onChange={(e) =>
                                                                            setNewYesNoQuestion(e.target.value)
                                                                        }
                                                                        style={{
                                                                            border: "2px solid #0d6efd",
                                                                            background: "#fffdf2",
                                                                            boxShadow:
                                                                                "0 0 0 2px rgba(13,110,253,.12)",
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />

                                                                    {/* -------------------------------------------- */}
                                                                    {/* YES CHOICE TEXT                              */}
                                                                    {/* -------------------------------------------- */}
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Enter Yes choice..."
                                                                        value={newYesChoice}
                                                                        onChange={(e) =>
                                                                            setNewYesChoice(e.target.value)
                                                                        }
                                                                        style={{
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />

                                                                    {/* -------------------------------------------- */}
                                                                    {/* NO CHOICE TEXT                               */}
                                                                    {/* -------------------------------------------- */}
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Enter No choice..."
                                                                        value={newNoChoice}
                                                                        onChange={(e) =>
                                                                            setNewNoChoice(e.target.value)
                                                                        }
                                                                        style={{
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />

                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        {/* -------------------------------------------- */}
                                                                        {/* ACCEPT NEW YES / NO QUESTION                 */}
                                                                        {/* -------------------------------------------- */}
                                                                        <button
                                                                            type="button"
                                                                            className="cfg-icon-btn"
                                                                            title="Add Yes/No question"
                                                                            onClick={acceptAddYesNoQuestion}
                                                                            disabled={
                                                                                !newYesNoQuestion.trim() ||
                                                                                !newYesChoice.trim() ||
                                                                                !newNoChoice.trim()
                                                                            }
                                                                        >
                                                                            <span
                                                                                className="material-icons cfg-icon"
                                                                                style={{ color: "#198754" }}
                                                                            >
                                                                                check
                                                                            </span>
                                                                        </button>

                                                                        {/* -------------------------------------------- */}
                                                                        {/* CANCEL NEW YES / NO QUESTION                 */}
                                                                        {/* -------------------------------------------- */}
                                                                        <button
                                                                            type="button"
                                                                            className="cfg-icon-btn"
                                                                            title="Cancel"
                                                                            onClick={cancelAddYesNoQuestion}
                                                                        >
                                                                            <span className="material-icons cfg-icon cfg-close-icon">
                                                                                close
                                                                            </span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="location-more-info"
                                                                    disabled
                                                                    style={{ marginRight: 8 }}
                                                                />
                                                                No — this location is complete
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* ------------------------------------------------ */}
                                            {/* ACTIONS FOR A TEXT ENTRY                        */}
                                            {/* Example: Describe Location, Train Car Number    */}
                                            {/* ------------------------------------------------ */}
                                            {selectedNode?.type === "text" && (
                                                <>
                                                    {/* ------------------------------------------------ */}
                                                    {/* TEXT ENTRY - EDIT TITLE / PROMPT                 */}
                                                    {/* Allows local editing of the Text Entry label and */}
                                                    {/* English prompt/placeholder.                      */}
                                                    {/* LOCAL STATE ONLY.                                */}
                                                    {/* ------------------------------------------------ */}
                                                    {editingText ? (
                                                        <div
                                                            style={{
                                                                width: "100%",
                                                                marginBottom: 12,
                                                            }}
                                                        >
                                                            {/* -------------------------------------------- */}
                                                            {/* TEXT ENTRY TITLE                              */}
                                                            {/* -------------------------------------------- */}
                                                            <div
                                                                style={{
                                                                    fontWeight: 600,
                                                                    marginBottom: 6,
                                                                }}
                                                            >
                                                                Field Text
                                                            </div>

                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={draftTextTitle}
                                                                onChange={(e) => setDraftTextTitle(e.target.value)}
                                                                style={{
                                                                    border: "2px solid #0d6efd",
                                                                    background: "#fffdf2",
                                                                    boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                                                                    marginBottom: 12,
                                                                }}
                                                            />

                                                            {/* -------------------------------------------- */}
                                                            {/* TEXT ENTRY PROMPT                             */}
                                                            {/* -------------------------------------------- */}
                                                            <div
                                                                style={{
                                                                    fontWeight: 600,
                                                                    marginBottom: 6,
                                                                }}
                                                            >
                                                                Prompt
                                                            </div>

                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Optional prompt shown to the reporter..."
                                                                value={draftTextPrompt}
                                                                onChange={(e) => setDraftTextPrompt(e.target.value)}
                                                                style={{
                                                                    marginBottom: 10,
                                                                }}
                                                            />

                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    gap: 8,
                                                                }}
                                                            >
                                                                {/* -------------------------------------------- */}
                                                                {/* ACCEPT TEXT ENTRY EDIT                       */}
                                                                {/* -------------------------------------------- */}
                                                                <button
                                                                    type="button"
                                                                    className="cfg-icon-btn"
                                                                    title="Save text edit"
                                                                    onClick={acceptEditText}
                                                                    disabled={!draftTextTitle.trim()}
                                                                >
                                                                    <span
                                                                        className="material-icons cfg-icon"
                                                                        style={{ color: "#198754" }}
                                                                    >
                                                                        check
                                                                    </span>
                                                                </button>

                                                                {/* -------------------------------------------- */}
                                                                {/* CANCEL TEXT ENTRY EDIT                       */}
                                                                {/* -------------------------------------------- */}
                                                                <button
                                                                    type="button"
                                                                    className="cfg-icon-btn"
                                                                    title="Cancel"
                                                                    onClick={cancelEditText}
                                                                >
                                                                    <span className="material-icons cfg-icon cfg-close-icon">
                                                                        close
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary"
                                                            onClick={startEditText}
                                                        >
                                                            Edit Text
                                                        </button>
                                                    )}

                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary"
                                                        disabled
                                                    >
                                                        Edit Translation
                                                    </button>

                                                    {/* ------------------------------------------------ */}
                                                    {/* TEXT ENTRY - TOGGLE REQUIRED                     */}
                                                    {/* Updates the Required setting in local state only.*/}
                                                    {/* ------------------------------------------------ */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={toggleRequired}
                                                    >
                                                        {selectedNode?.required
                                                            ? "Make Optional"
                                                            : "Make Required"}
                                                    </button>

                                                    {/* ------------------------------------------------ */}
                                                    {/* TEXT ENTRY - OPEN REMOVE CONFIRMATION            */}
                                                    {/* Removes the selected Text Entry from the local   */}
                                                    {/* working tree only after confirmation.            */}
                                                    {/* ------------------------------------------------ */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        onClick={() => setRemovingTextEntry(true)}
                                                    >
                                                        Remove Text
                                                    </button>
                                                    {/* ------------------------------------------------ */}
{/* TEXT ENTRY - REMOVE CONFIRMATION                 */}
{/* Confirms removal of the selected Text Entry.     */}
{/* LOCAL STATE ONLY.                                */}
{/* ------------------------------------------------ */}
{removingTextEntry && (
    <div
        style={{
            width: "100%",
            marginTop: 14,
            padding: 14,
            border: "1px solid #dc3545",
            borderRadius: 6,
            background: "#fff8f8",
        }}
    >
        <div
            style={{
                fontWeight: 700,
                marginBottom: 8,
            }}
        >
            Remove "{selectedNode?.title?.en_US || "this text entry"}"?
        </div>

        <div style={{ marginBottom: 10 }}>
            This text entry will be removed from this location.
        </div>

        <div
            style={{
                display: "flex",
                gap: 8,
            }}
        >
            <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRemovingTextEntry(false)}
            >
                Cancel
            </button>

            <button
                type="button"
                className="btn btn-danger"
                onClick={confirmRemoveTextEntry}
            >
                Remove Text
            </button>
        </div>
    </div>
)}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* -------------------------------------------------- */}
                                    {/* TEMPORARY DEVELOPMENT DETAIL                       */}
                                    {/* Helpful while building contextual actions.         */}
                                    {/* -------------------------------------------------- */}
                                    <div
                                        style={{
                                            marginTop: 14,
                                            paddingTop: 12,
                                            borderTop: "1px solid #eee",
                                            fontSize: 12,
                                            color: "#868e96",
                                        }}
                                    >
                                        Schema type: {selectedNode?.type || "unknown"}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )
            }
        </div >
    );
};

export default LocationsEditor;